// components/Documents/DocumentList.jsx (updated)
import React, { useState } from "react";
import {
  Box,
  Paper,
  Typography,
  Stack,
  Chip,
  IconButton,
  Tooltip,
  alpha,
} from "@mui/material";
import {
  InsertDriveFileOutlined,
  DeleteOutline,
  FileDownloadOutlined,
  Visibility as ViewIcon,
  PictureAsPdf as PdfIcon,
  Description as DocIcon,
  Image as ImageIcon,
} from "@mui/icons-material";
import { useQuery, useQueryClient } from "react-query";
import { useSnackbar } from "notistack";
import { documentService } from "../../api/services/documentService";
import { QK } from "../../base/qk";
import DataTable from "../Table/DataTable";
import DocumentUploader from "../CustomForm/DocumentUploader";
import CustomInput from "../CustomForm/CustomInput";
import DocumentPreviewDialog from "./DocumentPreviewDialog";
import DeleteConfirmDialog from "./DeleteConfirmDialog";

const DocumentList = ({ selectedCategory, searchQuery, onSearchChange }) => {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewUrl, setPreviewUrl] = useState("");
  const [previewType, setPreviewType] = useState("");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const { data: documentsData, isLoading } = useQuery(
    [QK.documents, selectedCategory, searchQuery],
    () => documentService.getDocuments(selectedCategory, searchQuery),
    {
      enabled: true,
      staleTime: 2 * 60 * 1000,
    }
  );

  const documents = Array.isArray(documentsData) ? documentsData : documentsData?.results || [];

  const handleUploadSuccess = async (result) => {
    const payload = {
      name: result.filename || result.file.name,
      url: result.url,
      file_name: result.filename || result.file.name,
      file_size: result.file_size || result.file.size,
      content_type: result.content_type || result.file.type,
      category: selectedCategory,
      description: "",
      is_public: true,
    };

    try {
      await documentService.createDocument(payload);
      queryClient.invalidateQueries([QK.documents]);
      queryClient.invalidateQueries([QK.documentCategories]);
      enqueueSnackbar("Document uploaded successfully", { variant: "success" });
    } catch (error) {
      enqueueSnackbar("Failed to save document", { variant: "error" });
    }
  };

  const handleUploadError = (error) => {
    enqueueSnackbar(error, { variant: "error" });
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    setIsDeleting(true);

    try {
      await documentService.deleteDocument(deleteId);
      queryClient.invalidateQueries([QK.documents]);
      enqueueSnackbar("Document deleted successfully", { variant: "success" });
      setDeleteDialogOpen(false);
      setDeleteId(null);
    } catch (error) {
      enqueueSnackbar("Failed to delete document", { variant: "error" });
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDownload = (url, name) => {
    if (url) {
      const link = document.createElement("a");
      link.href = url;
      link.target = "_blank";
      link.download = name || "document";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const handlePreview = (url, name) => {
    if (!url) return;
    const ext = url?.split(".").pop()?.toLowerCase() || "";
    const isViewable = ["pdf", "jpg", "jpeg", "png", "gif"].includes(ext);

    if (isViewable) {
      setPreviewUrl(url);
      setPreviewType(ext);
      setPreviewOpen(true);
    } else {
      handleDownload(url, name);
    }
  };

  const getFileIcon = (fileName) => {
    const ext = fileName?.split(".").pop()?.toLowerCase() || "";
    if (ext === "pdf") return <PdfIcon sx={{ fontSize: 20 }} />;
    if (["doc", "docx"].includes(ext)) return <DocIcon sx={{ fontSize: 20 }} />;
    if (["jpg", "jpeg", "png", "gif"].includes(ext)) return <ImageIcon sx={{ fontSize: 20 }} />;
    return <InsertDriveFileOutlined sx={{ fontSize: 20 }} />;
  };

  const getFileColor = (fileName) => {
    const ext = fileName?.split(".").pop()?.toLowerCase() || "";
    if (ext === "pdf") return "#EF4444";
    if (["doc", "docx"].includes(ext)) return "#3B82F6";
    if (["jpg", "jpeg", "png", "gif"].includes(ext)) return "#10B981";
    return "#6B7280";
  };

  const formatBytes = (bytes) => {
    if (!+bytes) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
  };

  const headers = [
    { key: "name", label: "File Name" },
    { key: "file_size", label: "Size" },
    { key: "category_name", label: "Category" },
    { key: "created_at", label: "Uploaded" },
  ];

  const renderCell = (item, header) => {
    if (header.key === "name") {
      const fileColor = getFileColor(item.file_name || item.name);
      return (
        <Stack direction="row" spacing={2} alignItems="center">
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: "8px",
              bgcolor: alpha(fileColor, 0.1),
              color: fileColor,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {getFileIcon(item.file_name || item.name)}
          </Box>
          <Typography variant="body2" fontWeight={600} color="#1A1A2E">
            {item.name}
          </Typography>
        </Stack>
      );
    }
    if (header.key === "file_size") {
      return (
        <Typography variant="body2" color="#6B7280">
          {formatBytes(item.file_size)}
        </Typography>
      );
    }
    if (header.key === "category_name") {
      return (
        <Chip
          label={item.category_name || "Uncategorized"}
          size="small"
          sx={{
            bgcolor: alpha("#2469F0", 0.08),
            color: "#2469F0",
            fontWeight: 500,
            fontSize: "11px",
          }}
        />
      );
    }
    if (header.key === "created_at") {
      return (
        <Typography variant="body2" color="#6B7280">
          {item.created_at
            ? new Date(item.created_at).toLocaleDateString("en-GB", {
                day: "2-digit",
                month: "short",
                year: "numeric",
              })
            : "-"}
        </Typography>
      );
    }
    return item[header.key] || "-";
  };

  const customActions = (item) => (
    <Box sx={{ display: "flex", gap: 0.25, justifyContent: "center", flexWrap: "wrap" }}>
      <Tooltip title="Preview" arrow>
        <IconButton
          size="small"
          onClick={() => handlePreview(item.url, item.name)}
          sx={{
            color: "#667085",
            p: 0.5,
            "&:hover": {
              color: "#2469F0",
              bgcolor: alpha("#2469F0", 0.06),
            },
          }}
        >
          <ViewIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </Tooltip>
      <Tooltip title="Download" arrow>
        <IconButton
          size="small"
          onClick={() => handleDownload(item.url, item.name)}
          sx={{
            color: "#667085",
            p: 0.5,
            "&:hover": {
              color: "#2469F0",
              bgcolor: alpha("#2469F0", 0.06),
            },
          }}
        >
          <FileDownloadOutlined sx={{ fontSize: 16 }} />
        </IconButton>
      </Tooltip>
      <Tooltip title="Delete" arrow>
        <IconButton
          size="small"
          onClick={() => {
            setDeleteId(item.id);
            setDeleteDialogOpen(true);
          }}
          sx={{
            color: "#667085",
            p: 0.5,
            "&:hover": {
              color: "#EF4444",
              bgcolor: alpha("#EF4444", 0.06),
            },
          }}
        >
          <DeleteOutline sx={{ fontSize: 16 }} />
        </IconButton>
      </Tooltip>
    </Box>
  );

  return (
    <Paper
      sx={{
        p: 3,
        borderRadius: "12px",
        border: "1px solid #E2E8F0",
        flex: 1,
        minWidth: 0,
      }}
    >
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
        <Typography variant="subtitle1" fontWeight={600} color="#1A1A2E">
          Documents
        </Typography>
        <DocumentUploader
          onUploadSuccess={handleUploadSuccess}
          onUploadError={handleUploadError}
          folder="documents"
          buttonText="Upload Document"
          variant="button"
          accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.xls,.xlsx"
          multiple={false}
        />
      </Box>

      <Box sx={{ mb: 2 }}>
        <CustomInput
          placeholder="Search documents..."
          value={searchQuery}
          onChange={(e) => onSearchChange(e.target.value)}
          fullWidth
          size="small"
        />
      </Box>

      <DataTable
        data={documents}
        headers={headers}
        renderCell={renderCell}
        customActions={customActions}
        loading={isLoading}
      />

      <DocumentPreviewDialog
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        url={previewUrl}
        fileType={previewType}
        onDownload={() => handleDownload(previewUrl, "document")}
      />

      <DeleteConfirmDialog
        open={deleteDialogOpen}
        onClose={() => {
          setDeleteDialogOpen(false);
          setDeleteId(null);
        }}
        onConfirm={handleDelete}
        isLoading={isDeleting}
      />
    </Paper>
  );
};

export default DocumentList;